import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import UploadDialog, { type Preset } from '@/components/documents/UploadDialog';
import { Checklist, DocCard, ResultCard } from '@/components/documents/DocParts';
import { qk } from '@/lib/queries';
import { errText, fetchCatalog, fetchPortalVault, openDoc, type Surface } from '@/lib/documents';

const SURFACE: Surface = { kind: 'portal' };

/** The clinic's side of the documents vault: what KAF PRO still needs, an upload for each,
 *  what the clinic gave, and KAF's contract and NDA to read. */
export default function PortalDocumentsPage() {
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: qk.docCatalog, queryFn: fetchCatalog, staleTime: 3600_000 });
  const vault = useQuery({
    queryKey: qk.portalDocuments, queryFn: fetchPortalVault,
    refetchInterval: (q) => (q.state.data?.documents.some((d) => d.ocr_status === 'pending' || d.ocr_status === 'running') ? 3000 : false),
  });
  const [preset, setPreset] = useState<Preset | null | undefined>(undefined);

  if (vault.isLoading || catalog.isLoading) return <p className="text-sm text-muted">Loading...</p>;
  if (vault.isError || !vault.data || !catalog.data) return <p className="text-sm text-red-400">{errText(vault.error, 'Could not load your documents.')}</p>;
  const v = vault.data;
  const mine = v.documents.filter((d) => d.from_client);
  const kaf = v.documents.filter((d) => !d.from_client);
  const open = (id: string) => openDoc(SURFACE, id).catch((e) => toast.error(errText(e, 'Could not open the file')));

  return (
    <div className="grid gap-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-white">Documents</h1>
          <p className="text-sm text-muted" dir="rtl">المستندات</p>
          <p className="text-sm text-muted mt-1">The papers we need to advertise for {v.client_name}. Files stay private to you and KAF PRO.</p>
        </div>
        <Button icon={<Upload size={14} />} onClick={() => setPreset(null)} data-testid="portal-doc-upload">Upload a document</Button>
      </div>

      <Card>
        <Checklist slots={v.needed} canUpload onUpload={(s) => setPreset({ catalogKey: s.suggest, docType: s.doc_type, authority: s.authority })}
          title="Documents we need from you" titleAr="المستندات المطلوبة منكم" />
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-white">What you uploaded <span className="text-xs text-muted font-normal" dir="rtl">ما قمتم برفعه</span></h2>
        <div className="grid gap-3 mt-3">
          {mine.length === 0 && <p className="text-sm text-muted">Nothing yet.</p>}
          {mine.map((d) => <DocCard key={d.id} doc={d} onOpen={open} result={<ResultCard doc={d} />} />)}
        </div>
      </Card>

      {kaf.length > 0 && (
        <Card>
          <h2 className="text-base font-semibold text-white">From KAF PRO <span className="text-xs text-muted font-normal" dir="rtl">من KAF PRO</span></h2>
          <div className="grid gap-3 mt-3">{kaf.map((d) => <DocCard key={d.id} doc={d} onOpen={open} />)}</div>
        </Card>
      )}

      <UploadDialog surface={SURFACE} catalog={catalog.data} open={preset !== undefined} preset={preset} clinicUpload
        onClose={() => setPreset(undefined)} onDone={() => { setPreset(undefined); qc.invalidateQueries({ queryKey: qk.portalDocuments }); }} />
    </div>
  );
}
